import type { Audience } from '../types/audience';

const MAX_INLINE_LABELS = 3;

export interface AudienceLabelUser {
  id: string;
  displayName: string;
}

/**
 * Render the user-facing label for one Audience document (ADR-120,
 * person-keyed per ADR-136).
 *
 * Display policy:
 *   - `name != null` → audience.name
 *   - `name == null && system == false` → joined labels of
 *     `members.includedAudienceIds`, truncated at MAX_INLINE_LABELS
 *     with "+N more"
 *   - `name == null && system == true` → resolved person's display name
 *     (personal audiences carry exactly one personId in members.personIds);
 *     falls back to "Individual" when the person is not in `personById`
 *   - missing/stale audience → "Audience unavailable"
 *
 * The point of ADR-120 is to hide the anonymous wrapper, not the targets.
 * Don't fall back to "Custom mix" unless every included ref is missing.
 *
 * Anonymous audiences are enforced "as leaves" server-side
 * (assertNoAnonymousIncludes), so the recursive call here only ever
 * descends one level — into a named audience or a personal audience.
 */
export function formatAudienceLabel(
  audience: Audience | undefined | null,
  audienceById: Map<string, Audience>,
  personById: Map<string, AudienceLabelUser>,
): string {
  if (!audience) return 'Audience unavailable';

  if (audience.name !== null) return audience.name;

  if (audience.system) {
    const personId = audience.members.personIds[0];
    if (!personId) return 'Individual';
    const person = personById.get(personId);
    return person?.displayName ?? 'Individual';
  }

  // Anonymous composition: render member labels.
  const ids = audience.members.includedAudienceIds;
  if (ids.length === 0) return 'Audience unavailable';

  const labels: string[] = [];
  let unresolved = 0;
  for (const id of ids) {
    const inc = audienceById.get(id);
    if (!inc) {
      unresolved += 1;
      continue;
    }
    labels.push(formatAudienceLabel(inc, audienceById, personById));
  }

  if (labels.length === 0) return 'Audience unavailable';

  if (labels.length <= MAX_INLINE_LABELS) {
    const remainder = unresolved > 0 ? `, +${unresolved} more` : '';
    return labels.join(', ') + remainder;
  }

  const head = labels.slice(0, MAX_INLINE_LABELS).join(', ');
  const overflow = labels.length - MAX_INLINE_LABELS + unresolved;
  return `${head}, +${overflow} more`;
}
